import { Box, Button, Modal, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import FormTextField from "../../../../components/forms/FormTextField";

const UpgradeUserToMSSPModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { upgradeUserToMSSP } = useAPI();
  const [isLoading, setIsLoading] = useState(false);

  const formInitialValues = {
    customerId: "",
    userEmail: "",
  };

  const formValidationSchema = yup.object().shape({
    customerId: yup.string().required("Customer ID is required"),
    userEmail: yup.string().required("User email is required"),
  });

  const onSubmit = async (values) => {
    setIsLoading(true);

    try {
      await upgradeUserToMSSP(values.customerId, values.userEmail);
      Toast.success("User upgraded successfully");
      handleClose();
    } catch (error) {
      Toast.error(error?.response?.content || "Failed to upgrade user");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
    >
      <Box
        sx={{
          width: 500,
          bgcolor: "background.paper",
          margin: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          overflowY: "auto",
          borderRadius: "10px",
        }}
      >
        <Box sx={{ width: 420, overflowY: "auto", padding: 2 }}>
          {/* Title */}
          <Typography
            variant="subtitle1"
            sx={{
              marginBottom: 3,
              color: colors.text_primary,
              textAlign: "center",
            }}
          >
            Upgrade user to MSSP
          </Typography>

          {/* Form */}
          <Formik
            onSubmit={onSubmit}
            initialValues={formInitialValues}
            validationSchema={formValidationSchema}
            enableReinitialize
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    paddingRight: 2,
                  }}
                >
                  <FormTextField
                    label="Customer ID"
                    fieldName="customerId"
                    required
                  />

                  <FormTextField
                    label="User email"
                    fieldName="userEmail"
                    required
                    helperText="for dev users <customer_id>@fake.com"
                  />
                </Box>

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    alignItems: "center",
                    marginTop: "32px",
                    marginBottom: "24px",
                  }}
                >
                  {/* Styling this button in-place until we have a button-text-inherit style button */}
                  <Button
                    variant="text"
                    onClick={handleClose}
                    sx={{
                      backgroundColor: colors.button_text_inherit_enabled_bg,
                      color: colors.button_text_inherit_enabled_text,
                      borderRadius: "10px",
                      minWidth: "64px",
                      padding: "7px 14.5px",
                      "&:hover": {
                        backgroundColor: colors.button_text_inherit_hovered_bg,
                      },
                    }}
                  >
                    <Typography variant="overline">Cancel</Typography>
                  </Button>

                  {/* Styling this button in-place until we have a button-contained-primary style button */}
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleSubmit();
                    }}
                    disabled={isLoading}
                    startIcon={isLoading ? <LoadingIcon size={16} /> : null}
                    sx={{
                      backgroundColor:
                        colors.button_contained_primary_enabled_bg,
                      color: colors.button_contained_primary_enabled_text,
                      borderRadius: "10px",
                      minWidth: "64px",
                      padding: "7px 14.5px",
                      "&:hover": {
                        backgroundColor:
                          colors.button_contained_primary_hovered_bg,
                      },
                    }}
                  >
                    <Typography variant="overline">
                      {isLoading ? "Upgrading..." : "Upgrade"}
                    </Typography>
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpgradeUserToMSSPModal;
